/**
 * Metadata Validation Utilities
 *
 * Validates AlgoRhythm metadata fields and other layer-specific requirements
 * to ensure data quality and recommendation engine compatibility.
 * Aligned with OpenAPI request schemas: maxLength 255, array min/max.
 */

import { LayerMetadata, SongsLayerMetadata, StarsLayerMetadata } from '../types/layerMetadata';
import {
  SONG_METADATA,
  ALGORHYTHM_METADATA,
  STAR_METADATA,
  validateStringMaxLength,
  validateArrayLength,
  validateArrayItems,
} from '../constants/apiMetadataConstraints';

// ============================================================================
// VALIDATION RESULT TYPES
// ============================================================================

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error';
}

export interface ValidationWarning {
  field: string;
  message: string;
  severity: 'warning';
}

// ============================================================================
// SONGS LAYER VALIDATION
// ============================================================================

/**
 * Validate Songs layer metadata for AlgoRhythm requirements
 */
export function validateSongsMetadata(metadata: SongsLayerMetadata): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Creator Description optional; warn if very short when provided
  if (metadata.creatorDescription && metadata.creatorDescription.trim().length > 0 && metadata.creatorDescription.length < 10) {
    warnings.push({
      field: 'creatorDescription',
      message: 'Song description is very short. Include song name, artist, and album for best AI results.',
      severity: 'warning',
    });
  }

  // Performance Context (OpenAPI Algorhythm: min 1 max 50)
  const pc = metadata.performanceContext;
  if (!pc || pc.length === 0) {
    errors.push({
      field: 'performanceContext',
      message: 'Performance Context is required (select at least 1)',
      severity: 'error',
    });
  } else {
    const msg = validateArrayLength(pc, { minItems: 1, maxItems: ALGORHYTHM_METADATA.arrayMaxItems }, 'Performance Context');
    if (msg) errors.push({ field: 'performanceContext', message: msg, severity: 'error' });
    else {
      const itemMsg = validateArrayItems(pc, ALGORHYTHM_METADATA.stringMaxLength, 'Performance Context');
      if (itemMsg) errors.push({ field: 'performanceContext', message: itemMsg, severity: 'error' });
    }
  }

  // Target Audience (OpenAPI Algorhythm: min 1 max 50)
  const ta = metadata.targetAudience;
  if (!ta || ta.length === 0) {
    errors.push({
      field: 'targetAudience',
      message: 'Target Audience is required (select at least 1)',
      severity: 'error',
    });
  } else {
    const msg = validateArrayLength(ta, { minItems: 1, maxItems: ALGORHYTHM_METADATA.arrayMaxItems }, 'Target Audience');
    if (msg) errors.push({ field: 'targetAudience', message: msg, severity: 'error' });
    else {
      const itemMsg = validateArrayItems(ta, ALGORHYTHM_METADATA.stringMaxLength, 'Target Audience');
      if (itemMsg) errors.push({ field: 'targetAudience', message: itemMsg, severity: 'error' });
    }
  }

  // Cultural Context (OpenAPI Algorhythm: min 1 max 50)
  const cc = metadata.culturalContext;
  if (!cc || cc.length === 0) {
    errors.push({
      field: 'culturalContext',
      message: 'Cultural Context is required (select at least 1)',
      severity: 'error',
    });
  } else {
    const msg = validateArrayLength(cc, { minItems: 1, maxItems: ALGORHYTHM_METADATA.arrayMaxItems }, 'Cultural Context');
    if (msg) errors.push({ field: 'culturalContext', message: msg, severity: 'error' });
    else {
      const itemMsg = validateArrayItems(cc, ALGORHYTHM_METADATA.stringMaxLength, 'Cultural Context');
      if (itemMsg) errors.push({ field: 'culturalContext', message: itemMsg, severity: 'error' });
    }
  }

  // Core song fields (OpenAPI: maxLength 255)
  if (!metadata.songName || metadata.songName.trim() === '') {
    errors.push({
      field: 'songName',
      message: 'Song name is required',
      severity: 'error',
    });
  } else {
    const msg = validateStringMaxLength(metadata.songName, SONG_METADATA.stringMaxLength, 'Song name');
    if (msg) errors.push({ field: 'songName', message: msg, severity: 'error' });
  }

  if (!metadata.artistName || metadata.artistName.trim() === '') {
    warnings.push({
      field: 'artistName',
      message: 'Artist name is recommended for better discoverability',
      severity: 'warning',
    });
  } else {
    const msg = validateStringMaxLength(metadata.artistName, SONG_METADATA.stringMaxLength, 'Artist name');
    if (msg) errors.push({ field: 'artistName', message: msg, severity: 'error' });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

// ============================================================================
// STARS LAYER VALIDATION
// ============================================================================

/**
 * Validate Stars layer metadata for AlgoRhythm requirements
 */
export function validateStarsMetadata(metadata: StarsLayerMetadata): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Creator Description optional; warn if very short when provided
  if (metadata.creatorDescription && metadata.creatorDescription.trim().length > 0 && metadata.creatorDescription.length < 10) {
    warnings.push({
      field: 'creatorDescription',
      message: 'Star description is very short. Include name, outfit, and context for best AI results.',
      severity: 'warning',
    });
  }

  // Mood (OpenAPI StarMetadataRequest: required, min 1 max 3)
  const mood = metadata.mood;
  if (!mood || mood.length === 0) {
    errors.push({
      field: 'mood',
      message: 'Mood is required (select at least 1)',
      severity: 'error',
    });
  } else {
    const msg = validateArrayLength(mood, { minItems: 1, maxItems: 3 }, 'Mood');
    if (msg) errors.push({ field: 'mood', message: msg, severity: 'error' });
    else {
      const itemMsg = validateArrayItems(mood, STAR_METADATA.stringMaxLength, 'Mood');
      if (itemMsg) errors.push({ field: 'mood', message: itemMsg, severity: 'error' });
    }
  }

  // Performance Context (OpenAPI StarMetadataRequest: array max 10)
  const pc = metadata.performanceContext;
  if (pc && pc.length > 0) {
    const msg = validateArrayLength(pc, { maxItems: STAR_METADATA.performanceContext.maxItems }, 'Performance Context');
    if (msg) errors.push({ field: 'performanceContext', message: msg, severity: 'error' });
    else {
      const itemMsg = validateArrayItems(pc, STAR_METADATA.stringMaxLength, 'Performance Context');
      if (itemMsg) errors.push({ field: 'performanceContext', message: itemMsg, severity: 'error' });
    }
  }

  // Target Audience (optional in current OpenAPI)
  const ta = metadata.targetAudience;
  if (ta && ta.length > 0) {
    const msg = validateArrayLength(ta, { maxItems: STAR_METADATA.targetAudience.maxItems }, 'Target Audience');
    if (msg) errors.push({ field: 'targetAudience', message: msg, severity: 'error' });
    else {
      const itemMsg = validateArrayItems(ta, STAR_METADATA.stringMaxLength, 'Target Audience');
      if (itemMsg) errors.push({ field: 'targetAudience', message: itemMsg, severity: 'error' });
    }
  }

  // Core star fields (OpenAPI: maxLength 255)
  if (!metadata.starName || metadata.starName.trim() === '') {
    errors.push({
      field: 'starName',
      message: 'Star name is required',
      severity: 'error',
    });
  } else {
    const msg = validateStringMaxLength(metadata.starName, STAR_METADATA.stringMaxLength, 'Star name');
    if (msg) errors.push({ field: 'starName', message: msg, severity: 'error' });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

// ============================================================================
// COMPOSITE LAYER VALIDATION
// ============================================================================

/**
 * Validate Composite layer - ensures all 5 components are present and valid
 */
export interface CompositeValidationResult extends ValidationResult {
  componentStatus: {
    songs: ComponentStatus;
    stars: ComponentStatus;
    looks: ComponentStatus;
    moves: ComponentStatus;
    worlds: ComponentStatus;
  };
  completenessPercentage: number;
}

export interface ComponentStatus {
  exists: boolean;
  hasAlgoRhythmMetadata: boolean;
  validationResult?: ValidationResult;
}

/**
 * Validate Composite asset metadata and components
 */
export function validateCompositeMetadata(
  composite: any,
  components: {
    songs?: any;
    stars?: any;
    looks?: any;
    moves?: any;
    worlds?: any;
  }
): CompositeValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Check for missing components
  const componentStatus: CompositeValidationResult['componentStatus'] = {
    songs: validateComponent(components.songs, 'songs'),
    stars: validateComponent(components.stars, 'stars'),
    looks: validateComponent(components.looks, 'looks'),
    moves: validateComponent(components.moves, 'moves'),
    worlds: validateComponent(components.worlds, 'worlds'),
  };

  // Count present components
  const presentComponents = Object.values(componentStatus).filter(c => c.exists).length;
  const completenessPercentage = (presentComponents / 5) * 100;

  // Add errors for missing components
  if (!componentStatus.songs.exists) {
    errors.push({
      field: 'components.songs',
      message: 'Songs (G) component is missing',
      severity: 'error',
    });
  }

  if (!componentStatus.stars.exists) {
    errors.push({
      field: 'components.stars',
      message: 'Stars (S) component is missing',
      severity: 'error',
    });
  }

  if (!componentStatus.looks.exists) {
    errors.push({
      field: 'components.looks',
      message: 'Looks (L) component is missing',
      severity: 'error',
    });
  }

  if (!componentStatus.moves.exists) {
    errors.push({
      field: 'components.moves',
      message: 'Moves (M) component is missing',
      severity: 'error',
    });
  }

  if (!componentStatus.worlds.exists) {
    errors.push({
      field: 'components.worlds',
      message: 'Worlds (W) component is missing',
      severity: 'error',
    });
  }

  // Check AlgoRhythm metadata completeness for Songs
  if (componentStatus.songs.exists && !componentStatus.songs.hasAlgoRhythmMetadata) {
    warnings.push({
      field: 'components.songs.algorhythmMetadata',
      message: 'Songs component is missing AlgoRhythm metadata (performance context, target audience, cultural context)',
      severity: 'warning',
    });
  }

  // Check AlgoRhythm metadata completeness for Stars
  if (componentStatus.stars.exists && !componentStatus.stars.hasAlgoRhythmMetadata) {
    warnings.push({
      field: 'components.stars.algorhythmMetadata',
      message: 'Stars component is missing AlgoRhythm metadata (performance context, target audience)',
      severity: 'warning',
    });
  }

  // Composite-level validations
  if (completenessPercentage < 100) {
    errors.push({
      field: 'composite',
      message: `Composite is incomplete (${completenessPercentage}% complete). All 5 components required.`,
      severity: 'error',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    componentStatus,
    completenessPercentage,
  };
}

/**
 * Helper function to validate a single component
 */
function validateComponent(component: any, layerType: string): ComponentStatus {
  if (!component) {
    return {
      exists: false,
      hasAlgoRhythmMetadata: false,
    };
  }

  let hasAlgoRhythmMetadata = false;
  let validationResult: ValidationResult | undefined;

  // Check for AlgoRhythm metadata based on layer type
  if (layerType === 'songs') {
    const songsMetadata = component.songsMetadata || component.layerMetadata || {};
    hasAlgoRhythmMetadata = !!(
      songsMetadata.performanceContext?.length > 0 &&
      songsMetadata.targetAudience?.length > 0 &&
      songsMetadata.culturalContext?.length > 0
    );
    validationResult = validateSongsMetadata(songsMetadata);
  } else if (layerType === 'stars') {
    const starsMetadata = component.starsMetadata || component.starMetadata || component.layerMetadata || {};
    hasAlgoRhythmMetadata = !!(
      starsMetadata.performanceContext?.length > 0 &&
      starsMetadata.targetAudience?.length > 0
    );
    validationResult = validateStarsMetadata(starsMetadata);
  }

  return {
    exists: true,
    hasAlgoRhythmMetadata,
    validationResult,
  };
}

// ============================================================================
// GENERIC LAYER VALIDATION
// ============================================================================

/**
 * Validate any layer metadata based on layerType
 */
export function validateLayerMetadata(metadata: LayerMetadata): ValidationResult {
  if (!metadata.layerType) {
    return {
      isValid: false,
      errors: [
        {
          field: 'layerType',
          message: 'Layer type is required',
          severity: 'error',
        },
      ],
      warnings: [],
    };
  }

  switch (metadata.layerType) {
    case 'songs':
      return validateSongsMetadata(metadata as SongsLayerMetadata);
    case 'stars':
      return validateStarsMetadata(metadata as StarsLayerMetadata);
    case 'composites':
      // Composite validation requires component data, so this is a basic check
      return {
        isValid: true,
        errors: [],
        warnings: [
          {
            field: 'composite',
            message: 'Use validateCompositeMetadata() for complete composite validation',
            severity: 'warning',
          },
        ],
      };
    default:
      // Other layers (Looks, Moves, Worlds) - basic validation
      return {
        isValid: true,
        errors: [],
        warnings: [],
      };
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format validation errors for display
 */
export function formatValidationErrors(result: ValidationResult): string {
  if (result.isValid) {
    return '';
  }

  const errorMessages = result.errors.map(e => `• ${e.message}`).join('\n');
  const warningMessages = result.warnings.map(w => `• ${w.message}`).join('\n');

  let output = '';
  if (errorMessages) {
    output += `Errors:\n${errorMessages}`;
  }
  if (warningMessages) {
    if (output) output += '\n\n';
    output += `Warnings:\n${warningMessages}`;
  }

  return output;
}

/**
 * Get summary of validation result
 */
export function getValidationSummary(result: ValidationResult): string {
  if (result.isValid) {
    return result.warnings.length > 0
      ? `✅ Valid (${result.warnings.length} warnings)`
      : '✅ Valid';
  }

  return `❌ Invalid (${result.errors.length} errors, ${result.warnings.length} warnings)`;
}

/**
 * Check if AlgoRhythm metadata is complete for a layer
 */
export function hasCompleteAlgoRhythmMetadata(metadata: LayerMetadata): boolean {
  if (metadata.layerType === 'songs') {
    const songs = metadata as SongsLayerMetadata;
    return !!(
      (songs.performanceContext?.length ?? 0) > 0 &&
      (songs.targetAudience?.length ?? 0) > 0 &&
      (songs.culturalContext?.length ?? 0) > 0
    );
  } else if (metadata.layerType === 'stars') {
    const stars = metadata as StarsLayerMetadata;
    return !!(
      (stars.performanceContext?.length ?? 0) > 0 &&
      (stars.targetAudience?.length ?? 0) > 0
    );
  }

  return false;
}
